export const storeFormSchema = {
  type: "object",
  properties: {
    name: {
      type: "string",
      title: "Store Name",
      description: "The display name of the store",
      minLength: 1,
    },
    slug: {
      type: "string",
      title: "Slug",
    },
    imageUrl: {
      type: ["string", "null"],
      title: "Store Image",
      description: "Store image",
    },
    categories: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Store Groups",
      description: "The store groups this store belongs to",
    },
    keywords: {
      type: "array",
      title: "Keywords",
      description: "List of keywords associated with this store",
      items: {
        type: "string",
      },
      minItems: 1,
    },
    metadata: {
      type: "object",
      title: "Store Details",
      properties: {
        store_id: { type: "string", title: "Store ID", minLength: 1 },
        name: { type: "string", title: "Store Name" },
        retailer: { type: "string", title: "Retailer", minLength: 1 },
        address: { type: "string", title: "Address", minLength: 1 },
        postcode: { type: "string", title: "Zip Code", minLength: 1 },
        city: { type: "string", title: "City", minLength: 1 },
        country: { type: "string", title: "Country", minLength: 1 },
        state: { type: "string", title: "State", minLength: 1 },
        district: { type: "string", title: "District" },
        google_maps_link: {
          title: "Google Maps Link",
          anyOf: [
            { type: "string", maxLength: 0 },
            { type: "string", pattern: "^https?://" },
            { type: "null" },
          ],
          message: "Must be a valid URL starting with http:// or https://",
        },
        geolocation: {
          type: "object",
          title: "Geolocation",
          properties: {
            latitude: {
              type: "number",
              title: "Latitude",
            },
            longitude: {
              type: "number",
              title: "Longitude",
            },
          },
          required: ["latitude", "longitude"],
        },
      },
      required: [
        "store_id",
        "retailer",
        "address",
        "postcode",
        "city",
        "country",
        "state",
        "geolocation",
      ],
    },
  },
  required: ["name", "keywords", "metadata"],
};

const createStoreProperties = { ...storeFormSchema.properties };
delete createStoreProperties.categories;

export const createStoreFormSchema = {
  ...storeFormSchema,
  properties: createStoreProperties,
  required: storeFormSchema.required.filter((field) => field !== "categories"),
};

export const storeInfoUISchema = {
  type: "VerticalLayout",
  elements: [
    {
      type: "Control",
      scope: "#/properties/imageUrl",
      component: "singleImageUpload",
      options: {
        description: "Drag and drop your store image here",
        shortDescription: "Store image (JPG, PNG)",
      },
    },
    {
      type: "HorizontalLayout",
      elements: [
        {
          type: "Control",
          scope: "#/properties/metadata/properties/store_id",
          options: { placeholder: "Enter store ID" },
        },
        {
          type: "Control",
          scope: "#/properties/name",
          options: {
            placeholder: "Enter store name",
          },
        },
        {
          type: "Control",
          scope: "#/properties/metadata/properties/retailer",
          options: { placeholder: "Enter retailer name" },
        },
      ],
    },
    {
      type: "HorizontalLayout",
      elements: [
        {
          type: "Control",
          scope: "#/properties/categories",
          component: "storeGroupSingleSelect",
          options: {
            multiple: true,
            placeholder: "Select store groups",
          },
        },
      ],
    },
  ],
};

export const createStoreInfoUISchema = {
  ...storeInfoUISchema,
  elements: storeInfoUISchema.elements.slice(0, 2),
};

const stateControl = {
  type: "Control",
  scope: "#/properties/metadata/properties/state",
  options: { placeholder: "Enter state" },
};

const districtControl = {
  type: "Control",
  scope: "#/properties/metadata/properties/district",
  options: { placeholder: "Enter district" },
};

export const getStoreAddressUISchema = ({ showState, showDistrict } = {}) => {
  const locationFields = [];
  if (showState !== false) locationFields.push(stateControl);
  if (showDistrict) locationFields.push(districtControl);

  return {
    type: "VerticalLayout",
    elements: [
      {
        type: "HorizontalLayout",
        elements: [
          {
            type: "Control",
            scope: "#/properties/metadata/properties/address",
            options: { placeholder: "Enter address" },
          },
          ...locationFields,
          {
            type: "Control",
            scope: "#/properties/metadata/properties/city",
            options: { placeholder: "Enter city" },
          },
          {
            type: "Control",
            scope: "#/properties/metadata/properties/postcode",
            options: { placeholder: "Enter zip code" },
          },
        ],
      },
      {
        type: "HorizontalLayout",
        elements: [
          {
            type: "Control",
            scope: "#/properties/metadata/properties/country",
            options: { placeholder: "Enter country" },
          },
          {
            type: "Control",
            scope: "#/properties/metadata/properties/google_maps_link",
            options: { placeholder: "Enter Google Maps link" },
          },
          {
            type: "Control",
            scope:
              "#/properties/metadata/properties/geolocation/properties/latitude",
            component: "decimalInput",
            maxDecimalPlaces: 6,
            options: { placeholder: "Enter latitude", step: 0.000001 },
          },
          {
            type: "Control",
            scope:
              "#/properties/metadata/properties/geolocation/properties/longitude",
            component: "decimalInput",
            maxDecimalPlaces: 6,
            options: { placeholder: "Enter longitude", step: 0.000001 },
          },
        ],
      },
    ],
  };
};

export const storeAddressUISchema = getStoreAddressUISchema({
  showState: true,
  showDistrict: false,
});

export const storeUISchema = {
  type: "VerticalLayout",
  elements: [...storeInfoUISchema.elements, ...storeAddressUISchema.elements],
};

export const STATUS_FILTER_OPTIONS = [
  { id: "ALL", displayText: "All" },
  { id: "ACTIVE", displayText: "Active" },
  { id: "INACTIVE", displayText: "Inactive" },
];

const statusFilterControl = {
  type: "Control",
  scope: "#/properties/status",
  component: "staticDropdownSelect",
  placeholder: "All",
  options: { childList: STATUS_FILTER_OPTIONS, selection: "single" },
};

export const storeGroupFilterSchema = {
  type: "object",
  properties: { status: { type: "string", title: "Status" } },
};

export const storeGroupFilterUISchema = {
  type: "HorizontalLayout",
  elements: [statusFilterControl],
};

export const storeFilterSchema = {
  type: "object",
  properties: {
    status: { type: "string", title: "Status" },
    name: { type: "string", title: "Store Name" },
    retailer: { type: "string", title: "Retailer" },
    city: { type: "string", title: "City" },
    state: { type: "string", title: "State" },
    district: { type: "string", title: "District" },
    country: { type: "string", title: "Country" },
    postcode: { type: "string", title: "Zip Code" },
    categories: { type: "array", title: "Store Group", items: { type: "string" } },
  },
};

const stateFilterControl = {
  type: "Control",
  scope: "#/properties/state",
  component: "distinctKeywordFieldSelect",
  options: { field: "metadata.state", placeholder: "Filter by state" },
};

const districtFilterControl = {
  type: "Control",
  scope: "#/properties/district",
  component: "distinctKeywordFieldSelect",
  options: { field: "metadata.district", placeholder: "Filter by district" },
};

const baseFilterElements = [
  {
    type: "Control",
    scope: "#/properties/name",
    options: { placeholder: "Filter by store name" },
  },
  {
    type: "Control",
    scope: "#/properties/retailer",
    component: "distinctKeywordFieldSelect",
    options: { field: "metadata.retailer", placeholder: "Filter by retailer" },
  },
  {
    type: "Control",
    scope: "#/properties/country",
    component: "distinctKeywordFieldSelect",
    options: { field: "metadata.country", placeholder: "Filter by country" },
  },
];

const trailingFilterElements = [
  {
    type: "Control",
    scope: "#/properties/city",
    component: "distinctKeywordFieldSelect",
    options: { field: "metadata.city", placeholder: "Filter by city" },
  },
  {
    type: "Control",
    scope: "#/properties/postcode",
    component: "distinctKeywordFieldSelect",
    options: { field: "metadata.postcode", placeholder: "Filter by postcode" },
  },
  {
    type: "Control",
    scope: "#/properties/categories",
    component: "storeGroupSingleSelect",
    options: { multiple: true },
    placeholder: "Filter by store group",
  },
  statusFilterControl,
];

export const getStoreFilterSchema = ({ showState, showDistrict } = {}) => {
  const properties = { ...storeFilterSchema.properties };
  if (showState === false) delete properties.state;
  if (!showDistrict) delete properties.district;
  return { ...storeFilterSchema, properties };
};

export const getStoreFilterUISchema = ({ showState, showDistrict } = {}) => {
  const locationFilters = [];
  if (showState !== false) locationFilters.push(stateFilterControl);
  if (showDistrict) locationFilters.push(districtFilterControl);
  return {
    type: "HorizontalLayout",
    elements: [...baseFilterElements, ...locationFilters, ...trailingFilterElements],
  };
};

export const storeFilterUISchema = getStoreFilterUISchema({
  showState: true,
  showDistrict: false,
});
